// LDT-706 · Lo que el cliente dice haber MOSTRADO al aceptar textos legales.
//
// El servidor (supabase/functions/_shared/legal-acceptance.ts, checkDisplayed)
// lo compara con lo vigente y solo registra la aceptación si coincide: si el
// texto cambió mientras la persona lo leía, responde `outdated` y hay que
// volver a mostrarlo. Mandar aquí un documento que no se pintó es registrar
// una aceptación falsa: pasa SOLO los que la pantalla enseña.
import { legalDoc, type LegalDocKey } from "@/config/legal";
import {
  VENUE_PRIVACY_DOC,
  type VenuePrivacyNoticeInputs,
  venuePrivacyNoticeVersion,
} from "../../supabase/functions/_shared/venue-privacy-notice";

/** Clave del aviso legal del local (settings.legal_notice) en el mapa. */
export const VENUE_NOTICE_DOC = "venue_legal_notice";

/** Misma normalización que el servidor: recortado; vacío = sin aviso. */
export function normalizeVenueNotice(text: string | null | undefined): string | null {
  const t = (text ?? "").trim();
  return t ? t : null;
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
  return Array.from(digest).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** LDT-710 · Clave del aviso de privacidad del parque en el mapa. */
export { VENUE_PRIVACY_DOC };

/**
 * {documento: versión} de lo que se ha mostrado. `venueNotice` es el texto del
 * aviso del local TAL COMO SE PINTÓ (null si no se mostró ninguno).
 * `privacyNotice` (LDT-710): las entradas del aviso de privacidad del parque
 * que se enlazó (null si no se enlazó); se manda su versión, calculada con la
 * misma función que el servidor.
 */
export async function shownLegalDocuments(
  docs: readonly LegalDocKey[],
  venueNotice?: string | null,
  privacyNotice?: VenuePrivacyNoticeInputs | null,
): Promise<Record<string, string>> {
  const shown: Record<string, string> = {};
  for (const d of docs) shown[d] = legalDoc(d).version;
  if (privacyNotice) shown[VENUE_PRIVACY_DOC] = await venuePrivacyNoticeVersion(privacyNotice);
  const notice = normalizeVenueNotice(venueNotice);
  if (notice) shown[VENUE_NOTICE_DOC] = await sha256Hex(notice);
  return shown;
}
